import React from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  FormControl,
  TextField,
  Select,
  MenuItem,
  Button,
} from "@mui/material";

const ClientForm = ({
  selectedClient,
  clientChanges,
  setClientChanges,
  handleSave,
  isSaveEnabled,
  setIsSaveEnabled,
  activeTab,
  setActiveTab,
  isAddMode,
  onCancel,
  validateEmail,
  validatePhone,
  formErrors,
  countrymaster,
}) => {
  // Format current timestamp
  const getCurrentTimestamp = () => {
    return new Date().toLocaleString('en-US', {
      timeZone: 'Asia/Kolkata',
      hour12: true,
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setClientChanges((prev) => ({ ...prev, [name]: value }));

    // Trigger specific validations and update isSaveEnabled
    let isValid = true;
    if (name === "EmailID") {
      isValid = validateEmail(value) && isValid;
    } else if (name === "Phone1") {
      isValid = validatePhone(value) && isValid;
    } else if (["FirstName", "LastName", "Country", "Address1"].includes(name)) {
      isValid = !!value && isValid;
    }

    // Check if all required fields are valid to enable the Save button
    const requiredFieldsValid =
      !!clientChanges.FirstName &&
      !!clientChanges.LastName &&
      !!clientChanges.Country &&
      !!clientChanges.Address1 &&
      validateEmail(clientChanges.EmailID || "") &&
      validatePhone(clientChanges.Phone1 || "");
    setIsSaveEnabled(requiredFieldsValid);
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  const handleSubmit = () => {
    // Validate all required fields before submitting
    const isValid =
      !!clientChanges.FirstName &&
      !!clientChanges.LastName &&
      !!clientChanges.Country &&
      !!clientChanges.Address1 &&
      validateEmail(clientChanges.EmailID || "") &&
      validatePhone(clientChanges.Phone1 || "");

    if (isValid) {
      handleSave(clientChanges);
    } else {
      setClientChanges((prev) => ({
        ...prev,
        errors: {
          FirstName: clientChanges.FirstName ? "" : "First Name is required",
          LastName: clientChanges.LastName ? "" : "Last Name is required",
          Country: clientChanges.Country ? "" : "Country is required",
          Address1: clientChanges.Address1 ? "" : "Address 1 is required",
          EmailID: formErrors.EmailID,
          Phone1: formErrors.Phone1,
        },
      }));
    }
  };

  return (
    <Box sx={{ p: 3, bgcolor: "background.paper", borderRadius: 2, boxShadow: 1 }}>
      <div className="table-header">
        <div className="header-text">
          <div className="head-text">{isAddMode ? "Add Client" : "Client Details"}</div>
        </div>
        <div className="head-btn">
          <div className="submit-btn-form">
            <Button className="submit-btn-more" onClick={onCancel} sx={{ marginRight: '5px' }}>
              Cancel
            </Button>
            {isAddMode && (
              <Button
                className="submit-btn-more"
                onClick={handleSubmit}
                disabled={!isSaveEnabled}
              >
                Submit
              </Button>
            )}
          </div>
        </div>
      </div>

      <Tabs
        value={activeTab}
        onChange={(e, newValue) => handleTabClick(newValue)}
        sx={{ mb: 2, "& .MuiTabs-indicator": { display: "none" } }} // Hide default indicator
      >
        <Tab
          className={activeTab === "clientDetails" ? "active" : ""}
          label="Buyer Details"
          value="clientDetails"
          sx={{
            borderBottom: "2px solid #de6b14 !important",
            "&:not(.active)": {
              borderBottom: 'none',
              border: 'none',
            },
            "&.active": {
              backgroundColor: "#de6b14",
              color: "white",
              borderBottom: '2px solid #de6b14 !important',
              border: 'none',
            },
          }}
        />
        <Tab
          className={activeTab === "companyDetails" ? "active" : ""}
          label="Company Details"
          value="companyDetails"
          sx={{
            borderBottom: "2px solid #de6b14 !important",
            "&:not(.active)": {
              borderBottom: 'none',
              border: 'none',
            },
            "&.active": {
              backgroundColor: "#de6b14",
              color: "white",
              borderBottom: '2px solid #de6b14 !important',
              border: 'none',
            },
          }}
        />
      </Tabs>

      {activeTab === "clientDetails" && (
        <Box>
          <div className="row g-2">
            <div className="col-md-4">
              <FormControl fullWidth>
                <Typography variant="body2">
                  First Name <span className="mandatory">*</span>
                </Typography>
                <TextField
                  name="FirstName"
                  value={clientChanges.FirstName || ""}
                  onChange={handleInputChange}
                  size="small"
                  sx={{ backgroundColor: "white" }}
                  placeholder="First Name"
                  inputProps={{ "aria-label": "First Name" }}
                  error={!!formErrors.FirstName}
                  helperText={formErrors.FirstName}
                />
              </FormControl>
            </div>
            <div className="col-md-4">
              <FormControl fullWidth>
                <Typography variant="body2">
                  Last Name <span className="mandatory">*</span>
                </Typography>
                <TextField
                  name="LastName"
                  value={clientChanges.LastName || ""}
                  onChange={handleInputChange}
                  size="small"
                  sx={{ backgroundColor: "white" }}
                  placeholder="Last Name"
                  inputProps={{ "aria-label": "Last Name" }}
                  error={!!formErrors.LastName}
                  helperText={formErrors.LastName}
                />
              </FormControl>
            </div>
            <div className="col-md-4">
              <FormControl fullWidth>
                <Typography variant="body2">
                  Gender
                </Typography>
                <Select
                  name="Gender"
                  value={clientChanges.Gender || ""}
                  onChange={handleInputChange}
                  displayEmpty
                  sx={{ backgroundColor: "white" }}
                  inputProps={{ "aria-label": "Select Gender" }}
                >
                  <MenuItem value="" disabled>
                    Select Gender
                  </MenuItem>
                  <MenuItem value="M">Male</MenuItem>
                  <MenuItem value="F">Female</MenuItem>
                  <MenuItem value="O">Other</MenuItem>
                </Select>
              </FormControl>
            </div>
            <div className="col-md-4">
              <FormControl fullWidth>
                <Typography variant="body2">
                  Email <span className="mandatory">*</span>
                </Typography>
                <TextField
                  name="EmailID"
                  value={clientChanges.EmailID || ""}
                  onChange={handleInputChange}
                  size="small"
                  sx={{ backgroundColor: "white" }}
                  placeholder="Email"
                  inputProps={{ "aria-label": "Email", readOnly: !isAddMode }}
                  error={!!formErrors.EmailID}
                  helperText={formErrors.EmailID}
                />
              </FormControl>
            </div>
            <div className="col-md-4">
              <FormControl fullWidth>
                <Typography variant="body2">
                  State
                </Typography>
                <TextField
                  name="State"
                  value={clientChanges.State || ""}
                  onChange={handleInputChange}
                  size="small"
                  sx={{ backgroundColor: "white" }}
                  placeholder="State"
                  inputProps={{ "aria-label": "State" }}
                />
              </FormControl>
            </div>
            <div className="col-md-4">
              <FormControl fullWidth>
                <Typography variant="body2">
                  PIN/ZIP
                </Typography>
                <TextField
                  name="ZipCode"
                  value={clientChanges.ZipCode || ""}
                  onChange={handleInputChange}
                  size="small"
                  sx={{ backgroundColor: "white" }}
                  placeholder="PIN/ZIP"
                  inputProps={{ "aria-label": "PIN/ZIP" }}
                />
              </FormControl>
            </div>
            <div className="col-md-4">
              <FormControl fullWidth>
                <Typography variant="body2">
                  Phone 1 <span className="mandatory">*</span>
                </Typography>
                <TextField
                  name="Phone1"
                  value={clientChanges.Phone1 || ""}
                  onChange={handleInputChange}
                  size="small"
                  sx={{ backgroundColor: "white" }}
                  placeholder="Phone 1"
                  inputProps={{ "aria-label": "Phone 1" }}
                  error={!!formErrors.Phone1}
                  helperText={formErrors.Phone1}
                />
              </FormControl>
            </div>
            <div className="col-md-4">
              <FormControl fullWidth>
                <Typography variant="body2">
                  Phone 2
                </Typography>
                <TextField
                  name="Phone2"
                  value={clientChanges.Phone2 || ""}
                  onChange={handleInputChange}
                  size="small"
                  sx={{ backgroundColor: "white" }}
                  placeholder="Phone 2"
                  inputProps={{ "aria-label": "Phone 2" }}
                />
              </FormControl>
            </div>
            <div className="col-md-4">
              <FormControl fullWidth>
                <Typography variant="body2">
                  Country <span className="mandatory">*</span>
                </Typography>
                <Select
                  name="Country"
                  value={clientChanges.Country || ""}
                  onChange={handleInputChange}
                  displayEmpty
                  sx={{ backgroundColor: "white" }}
                  inputProps={{ "aria-label": "Select Country" }}
                  error={!!formErrors.Country}
                >
                  <MenuItem value="" disabled>
                    Select Country
                  </MenuItem>
                  {countrymaster.map((p) => (
                    <MenuItem key={p.ID} value={p.Value}>
                      {p.Value}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.Country && (
                  <Typography variant="caption" color="error">
                    {formErrors.Country}
                  </Typography>
                )}
              </FormControl>
            </div>
            <div className="col-md-4">
              <FormControl fullWidth>
                <Typography variant="body2">
                  LinkedIn
                </Typography>
                <TextField
                  name="LinkedIn"
                  value={clientChanges.LinkedIn || ""}
                  onChange={handleInputChange}
                  size="small"
                  sx={{ backgroundColor: "white" }}
                  placeholder="LinkedIn"
                  inputProps={{ "aria-label": "LinkedIn" }}
                />
              </FormControl>
            </div>
            <div className="col-md-4">
              <FormControl fullWidth>
                <Typography variant="body2">
                  Twitter
                </Typography>
                <TextField
                  name="Twitter"
                  value={clientChanges.Twitter || ""}
                  onChange={handleInputChange}
                  size="small"
                  sx={{ backgroundColor: "white" }}
                  placeholder="Twitter"
                  inputProps={{ "aria-label": "Twitter" }}
                />
              </FormControl>
            </div>
            <div className="col-md-4">
              <FormControl fullWidth>
                <Typography variant="body2">
                  Website
                </Typography>
                <TextField
                  name="Website"
                  value={clientChanges.Website || ""}
                  onChange={handleInputChange}
                  size="small"
                  sx={{ backgroundColor: "white" }}
                  placeholder="Website"
                  inputProps={{ "aria-label": "Website" }}
                />
              </FormControl>
            </div>
            <div className="col-md-4">
              <FormControl fullWidth>
                <Typography variant="body2">
                  Address 1 <span className="mandatory">*</span>
                </Typography>
                <TextField
                  name="Address1"
                  value={clientChanges.Address1 || ""}
                  onChange={handleInputChange}
                  size="small"
                  sx={{ backgroundColor: "white" }}
                  placeholder="Address 1"
                  inputProps={{ "aria-label": "Address 1" }}
                  error={!!formErrors.Address1}
                  helperText={formErrors.Address1}
                />
              </FormControl>
            </div>
            <div className="col-md-4">
              <FormControl fullWidth>
                <Typography variant="body2">
                  Address 2
                </Typography>
                <TextField
                  name="Address2"
                  value={clientChanges.Address2 || ""}
                  onChange={handleInputChange}
                  size="small"
                  sx={{ backgroundColor: "white" }}
                  placeholder="Address 2"
                  inputProps={{ "aria-label": "Address 2" }}
                />
              </FormControl>
            </div>
            <div className="col-md-4">
              <FormControl fullWidth>
                <Typography variant="body2">
                  Address 3
                </Typography>
                <TextField
                  name="Address3"
                  value={clientChanges.Address3 || ""}
                  onChange={handleInputChange}
                  size="small"
                  sx={{ backgroundColor: "white" }}
                  placeholder="Address 3"
                  inputProps={{ "aria-label": "Address 3" }}
                />
              </FormControl>
            </div>
          </div>
        </Box>
      )}

      {activeTab === "companyDetails" && (
        <Box>
          <div className="row g-2">
            <div className="col-md-4">
              <FormControl fullWidth>
                <Typography variant="body2">
                  Company Name
                </Typography>
                <TextField
                  name="CompanyName"
                  value={clientChanges.CompanyName || ""}
                  onChange={handleInputChange}
                  size="small"
                  sx={{ backgroundColor: "white" }}
                  placeholder="Company Name"
                  inputProps={{ "aria-label": "Company Name" }}
                />
              </FormControl>
            </div>
            <div className="col-md-4">
              <FormControl fullWidth>
                <Typography variant="body2">
                  Website
                </Typography>
                <TextField
                  name="CompanyWebsite"
                  value={clientChanges.CompanyWebsite || ""}
                  onChange={handleInputChange}
                  size="small"
                  sx={{ backgroundColor: "white" }}
                  placeholder="Website"
                  inputProps={{ "aria-label": "Website" }}
                />
              </FormControl>
            </div>
            <div className="col-md-4">
              <FormControl fullWidth>
                <Typography variant="body2">
                  Email
                </Typography>
                <TextField
                  name="CompanyEmail"
                  value={clientChanges.CompanyEmail || ""}
                  onChange={handleInputChange}
                  size="small"
                  sx={{ backgroundColor: "white" }}
                  placeholder="Email"
                  inputProps={{ "aria-label": "Email" }}
                />
              </FormControl>
            </div>
            <div className="col-md-4">
              <FormControl fullWidth>
                <Typography variant="body2">
                  Address 1
                </Typography>
                <TextField
                  name="CompanyAddress1"
                  value={clientChanges.CompanyAddress1 || ""}
                  onChange={handleInputChange}
                  size="small"
                  sx={{ backgroundColor: "white" }}
                  placeholder="Address 1"
                  inputProps={{ "aria-label": "Address 1" }}
                />
              </FormControl>
            </div>
            <div className="col-md-4">
              <FormControl fullWidth>
                <Typography variant="body2">
                  Address 2
                </Typography>
                <TextField
                  name="CompanyAddress2"
                  value={clientChanges.CompanyAddress2 || ""}
                  onChange={handleInputChange}
                  size="small"
                  sx={{ backgroundColor: "white" }}
                  placeholder="Address 2"
                  inputProps={{ "aria-label": "Address 2" }}
                />
              </FormControl>
            </div>
            <div className="col-md-4">
              <FormControl fullWidth>
                <Typography variant="body2">
                  Address 3
                </Typography>
                <TextField
                  name="CompanyAddress3"
                  value={clientChanges.CompanyAddress3 || ""}
                  onChange={handleInputChange}
                  size="small"
                  sx={{ backgroundColor: "white" }}
                  placeholder="Address 3"
                  inputProps={{ "aria-label": "Address 3" }}
                />
              </FormControl>
            </div>
            <div className="col-md-4">
              <FormControl fullWidth>
                <Typography variant="body2">
                  PIN/ZIP
                </Typography>
                <TextField
                  name="CompanyZipCode"
                  value={clientChanges.CompanyZipCode || ""}
                  onChange={handleInputChange}
                  size="small"
                  sx={{ backgroundColor: "white" }}
                  placeholder="PIN/ZIP"
                  inputProps={{ "aria-label": "PIN/ZIP" }}
                />
              </FormControl>
            </div>
            <div className="col-md-4">
              <FormControl fullWidth>
                <Typography variant="body2">
                  LinkedIn
                </Typography>
                <TextField
                  name="CompanyLinkedIn"
                  value={clientChanges.CompanyLinkedIn || ""}
                  onChange={handleInputChange}
                  size="small"
                  sx={{ backgroundColor: "white" }}
                  placeholder="LinkedIn"
                  inputProps={{ "aria-label": "LinkedIn" }}
                />
              </FormControl>
            </div>
            <div className="col-md-4">
              <FormControl fullWidth>
                <Typography variant="body2">
                  Twitter
                </Typography>
                <TextField
                  name="CompanyTwitter"
                  value={clientChanges.CompanyTwitter || ""}
                  onChange={handleInputChange}
                  size="small"
                  sx={{ backgroundColor: "white" }}
                  placeholder="Twitter"
                  inputProps={{ "aria-label": "Twitter" }}
                />
              </FormControl>
            </div>
            <div className="col-md-4">
              <FormControl fullWidth>
                <Typography variant="body2">
                  Company Phone 1
                </Typography>
                <TextField
                  name="CompanyPhone1"
                  value={clientChanges.CompanyPhone1 || ""}
                  onChange={handleInputChange}
                  size="small"
                  sx={{ backgroundColor: "white" }}
                  placeholder="Company Phone 1"
                  inputProps={{ "aria-label": "Company Phone 1" }}
                />
              </FormControl>
            </div>
            <div className="col-md-4">
              <FormControl fullWidth>
                <Typography variant="body2">
                  Company Phone 2
                </Typography>
                <TextField
                  name="CompanyPhone2"
                  value={clientChanges.CompanyPhone2 || ""}
                  onChange={handleInputChange}
                  size="small"
                  sx={{ backgroundColor: "white" }}
                  placeholder="Company Phone 2"
                  inputProps={{ "aria-label": "Company Phone 2" }}
                />
              </FormControl>
            </div>
            <div className="col-md-4">
              <FormControl fullWidth>
                <Typography variant="body2">
                  Company Phone 3
                </Typography>
                <TextField
                  name="CompanyPhone3"
                  value={clientChanges.CompanyPhone3 || ""}
                  onChange={handleInputChange}
                  size="small"
                  sx={{ backgroundColor: "white" }}
                  placeholder="Company Phone 3"
                  inputProps={{ "aria-label": "Company Phone 3" }}
                />
              </FormControl>
            </div>
            <div className="col-md-4">
              <FormControl fullWidth>
                <Typography variant="body2">
                  Company Phone 4
                </Typography>
                <TextField
                  name="CompanyPhone4"
                  value={clientChanges.CompanyPhone4 || ""}
                  onChange={handleInputChange}
                  size="small"
                  sx={{ backgroundColor: "white" }}
                  placeholder="Company Phone 4"
                  inputProps={{ "aria-label": "Company Phone 4" }}
                />
              </FormControl>
            </div>
          </div>
        </Box>
      )}
    </Box>
  );
};

export default ClientForm;